// src/components/CategoryListLayer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  RiAddLine,
  RiEdit2Line,
  RiDeleteBin6Line,
  RiSearchLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "react-icons/ri";

import { getCategories, deleteCategory } from "../services/categoryService";
import "./OutletsList.css";

const CategoryListLayer = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔍 search
  const [search, setSearch] = useState("");

  // 📄 pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    loadCategories();
  }, []);

  // ⚠️ LOGIC UNCHANGED
  const loadCategories = async () => {
    try {
      setLoading(true);
      const res = await getCategories();

      if (res.status === "success") {
        setCategories(res.categories || []); // ✅ DO NOT CHANGE
      } else {
        toast.error(res.message || "Failed to load categories");
      }
    } catch {
      toast.error("API Error while loading categories");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;

    try {
      const res = await deleteCategory(id);
      if (res.status === "success") {
        toast.success("Category deleted");
        loadCategories();
      } else toast.error(res.message);
    } catch {
      toast.error("API Error");
    }
  };

  // 🔍 SEARCH (UI only)
  const filteredData = useMemo(() => {
    return categories.filter((c) =>
      `${c.name} ${c.description || ""}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [categories, search]);

  // 📄 PAGINATION (UI only)
  const effectivePageSize =
    pageSize === "all" ? filteredData.length : pageSize;

  const totalPages =
    effectivePageSize === 0
      ? 1
      : Math.ceil(filteredData.length / effectivePageSize);

  const paginatedData =
    pageSize === "all"
      ? filteredData
      : filteredData.slice(
          (currentPage - 1) * effectivePageSize,
          currentPage * effectivePageSize
        );

  return (
    <div className="ul-page">
      {/* HEADER */}
      <div className="ul-header">
        <h4>Category List</h4>
        <Link to="/add-category" className="ul-add-btn">
          <RiAddLine /> Add Category
        </Link>
      </div>

      {/* FILTER BAR */}
      <div className="ul-filters">
        <div className="ul-search">
          <RiSearchLine />
          <input
            placeholder="Search category..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="ul-pagesize">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(
                e.target.value === "all"
                  ? "all"
                  : Number(e.target.value)
              );
              setCurrentPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value="all">All</option>
          </select>
          <span>entries</span>
        </div>
      </div>

      {/* TABLE */}
      <div className="ul-table-wrapper">
        <table className="ul-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Category Name</th>
              <th>Description</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center">Loading...</td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  No categories found
                </td>
              </tr>
            ) : (
              paginatedData.map((c, i) => (
                <tr key={c.id}>
                  <td>
                    {pageSize === "all"
                      ? i + 1
                      : (currentPage - 1) * effectivePageSize + i + 1}
                  </td>
                  <td>{c.name}</td>
                  <td>{c.description || "-"}</td>
                  <td className="ul-actions">
                    <Link to={`/edit-category/${c.id}`} className="ul-btn edit">
                      <RiEdit2Line /> Edit
                    </Link>
                    <button
                      className="ul-btn delete"
                      onClick={() => handleDelete(c.id)}
                    >
                      <RiDeleteBin6Line /> Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {pageSize !== "all" && totalPages > 1 && (
        <div className="ul-pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <RiArrowLeftSLine />
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            <RiArrowRightSLine />
          </button>
        </div>
      )}
    </div>
  );
};

export default CategoryListLayer;
